import { MutableRefObject, useCallback, useRef, useState } from 'react';

interface Options {
  /** Minimum panel width in rem. */
  min: number;
  /** Absolute maximum panel width in rem (design cap). */
  max: number;
  /**
   * Optional ref whose `.current` is read on every mousemove as the effective
   * upper bound, allowing the caller to tighten the max dynamically (e.g. when
   * the viewport or a sibling panel changes) without recreating onMouseDown.
   * Falls back to `max` when not provided.
   */
  maxRef?: MutableRefObject<number>;
  /** Initial panel width in rem. */
  initial: number;
  /**
   * Reserved space (in rem) to the RIGHT of this panel — e.g. the edge-strip
   * width. When provided, the panel's right boundary is computed as
   * `window.innerWidth - rightEdgePadRem * rootFsPx` rather than reading
   * `getBoundingClientRect().right`.
   *
   * Using the fixed viewport-based boundary avoids a positive-feedback loop
   * that occurs when a sibling element (e.g. the workspace) has a non-zero
   * minimum rendered size: DOM measurement drifts rightward as the panel
   * overflows, causing each mousemove to report a larger width than intended,
   * which in turn pushes the edge strip further off-screen.
   */
  rightEdgePadRem?: number;
}

/** Returns the root font size in px (used to convert rem ↔ px). */
const getRootFontSizePx = (): number =>
  parseFloat(getComputedStyle(document.documentElement).fontSize);

/**
 * Resize hook for a right-side panel whose left edge is the drag handle.
 * Mirrors useResizableSidebar but measures from the panel's right boundary.
 *
 * Width is stored and returned in rem. Mouse event coordinates (always px)
 * are converted at runtime via the document root font size so the constraints
 * scale with the user's browser font-size preference.
 */
export const useResizablePanel = ({
  min,
  max,
  maxRef,
  initial,
  rightEdgePadRem,
}: Options) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const widthRef = useRef(initial);
  const rafRef = useRef<number | null>(null);

  const [width, setWidth] = useState(initial);

  const onMouseDown = useCallback(() => {
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (e: MouseEvent) => {
      if (!panelRef.current) return;

      const rootFsPx = getRootFontSizePx();
      // Compute the panel's right boundary. Using a viewport-based constant
      // (viewport − reserved edge space) is intentional: reading
      // getBoundingClientRect().right drifts when a sibling has a non-zero
      // minimum rendered size (e.g. workspace padding), creating a positive
      // feedback loop that pushes the edge strip progressively off-screen.
      const panelRightPx =
        rightEdgePadRem !== undefined
          ? window.innerWidth - rightEdgePadRem * rootFsPx
          : panelRef.current.getBoundingClientRect().right;
      const nextRem = (panelRightPx - e.clientX) / rootFsPx;
      const effectiveMax = maxRef ? maxRef.current : max;
      const clamped = Math.min(effectiveMax, Math.max(min, nextRem));

      widthRef.current = clamped;

      if (rafRef.current) return;

      rafRef.current = requestAnimationFrame(() => {
        if (!panelRef.current) {
          rafRef.current = null;
          return;
        }

        const fs = getRootFontSizePx();
        panelRef.current.style.width = `${widthRef.current}rem`;

        // Overflow guard — only active when a fixed right boundary is in use.
        //
        // The geometric clamp in onMouseMove (maxRef.current) cannot know the
        // workspace's min-content width, so it may allow a panel width that
        // causes the workspace to overflow its minimum and push the edge strip
        // off-screen. After applying the intended width we force a reflow and
        // check whether the panel's right edge has drifted past the intended
        // boundary. If it has, we derive the true maximum from the panel's
        // actual LEFT edge (which correctly reflects the workspace constraint —
        // the workspace can't push the panel's left edge further right than its
        // min-content allows) and apply the corrected width immediately so the
        // edge strip is never visually displaced.
        if (rightEdgePadRem !== undefined) {
          const rect = panelRef.current.getBoundingClientRect();
          const intendedRight = window.innerWidth - rightEdgePadRem * fs;
          if (rect.right > intendedRight + 1) {
            // intendedRight - rect.left = the actual space available for the
            // panel given the workspace's current (possibly compressed) width.
            const corrected = Math.max(min, (intendedRight - rect.left) / fs);
            widthRef.current = corrected;
            panelRef.current.style.width = `${corrected}rem`;
          }
        }

        rafRef.current = null;
      });
    };

    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      // Cancel any pending RAF so it cannot overwrite widthRef after
      // setWidth has already committed the final value to React state.
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      // Apply the same overflow correction synchronously so the value
      // committed to React state (and persisted to localStorage) is the
      // corrected width, not the pre-correction value from onMouseMove.
      if (panelRef.current && rightEdgePadRem !== undefined) {
        const fs = getRootFontSizePx();
        panelRef.current.style.width = `${widthRef.current}rem`;
        const rect = panelRef.current.getBoundingClientRect();
        const intendedRight = window.innerWidth - rightEdgePadRem * fs;
        if (rect.right > intendedRight + 1) {
          const corrected = Math.max(min, (intendedRight - rect.left) / fs);
          widthRef.current = corrected;
          panelRef.current.style.width = `${corrected}rem`;
        }
      }

      const finalWidth = Math.min(max, Math.max(min, widthRef.current));
      setWidth(finalWidth);

      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [min, max, rightEdgePadRem]);

  return { panelRef, width, widthRef, setWidth, onMouseDown };
};
